import { Box, Drawer } from '@mui/material';

import React, { useEffect, useMemo, useState } from 'react';

import { ReactComponent as Resize } from 'common/assets/images/resize-handle.svg';
import { MODULES } from 'common/constants';

import styles from './SideDrawer.module.scss';

interface ISideDrawerProps {
	children: React.ReactNode;
	className?: string;
	customClass?: string;
	drawerState: boolean;
	shouldExpand?: boolean;
	pageModule?: string;
	isBorderRadius?: boolean;
	allowDrag?: boolean;
}

const { drawerPaperRadius } = styles;
const paperRadiusStyle = {
	sx: {
		borderTopLeftRadius: drawerPaperRadius,
		borderBottomLeftRadius: drawerPaperRadius,
	},
};

const SideDrawer = (props: ISideDrawerProps) => {
	const {
		children,
		className,
		drawerState,
		shouldExpand = false,
		pageModule,
		customClass,
		isBorderRadius = false,
		allowDrag = true,
	} = props;

	const [newWidth, setNewWidth] = useState<object>({});
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	const vw = useMemo(() => windowWidth / 100, [windowWidth]);

	const paperWidthMax = useMemo(() => 100 * vw - 3 * vw, [windowWidth]);

	const paperWidthMin = useMemo(() => 50 * vw, [windowWidth]);

	useEffect(() => {
		const windowResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener('resize', windowResize);
		return () => window.removeEventListener('resize', windowResize);
	}, []);

	useEffect(() => {
		let paperWidth = 50 * vw;
		if (shouldExpand) {
			paperWidth = paperWidthMax;
		}
		setNewWidth({ width: paperWidth });
	}, [shouldExpand, vw]);

	const handleResizeMove = (e: any) => {
		const offsetRight =
			document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
		if (offsetRight >= paperWidthMin && offsetRight <= paperWidthMax) {
			if (allowDrag) {
				setNewWidth({ width: offsetRight });
			}
		}
	};

	const handleMouseDown = () => {
		const handleMouseMove = (e: any) => {
			handleResizeMove(e);
			e.preventDefault();
		};
		const handleMouseUp = () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
	};

	const handleTouchStart = () => {
		const handleTouchMove = (e: any) => {
			handleResizeMove(e.touches[0]);
		};
		const handleTouchEnd = () => {
			window.removeEventListener('touchmove', handleTouchMove);
			window.removeEventListener('touchend', handleTouchEnd);
		};
		window.addEventListener('touchmove', handleTouchMove);
		window.addEventListener('touchend', handleTouchEnd);
	};

	const [isOpen, setIsOpen] = useState(false);
	useEffect(() => setIsOpen(drawerState), [drawerState]);

	return (
		<div>
			{pageModule !== MODULES.ASK_RISK_LINK ? (
				<Drawer
					data-testid="test-drawer"
					hideBackdrop
					className={className}
					open={isOpen}
					anchor="right"
					PaperProps={isBorderRadius ? paperRadiusStyle : {}}
				>
					<Box
						className={
							customClass?.length ? customClass : styles['custom-width']
						}
					>
						{children}
					</Box>
				</Drawer>
			) : (
				<Drawer
					data-testid="test-drawer"
					className={className}
					open={isOpen}
					anchor="right"
					PaperProps={{
						classes: {
							root: `${styles['drawer-paper']}`,
						},
						style: newWidth,
					}}
				>
					<Box className={styles['paper-box']}>
						<Resize
							onMouseDown={handleMouseDown}
							onTouchStart={handleTouchStart}
							className={styles['resize-handle']}
							data-testid="resize-handle"
						/>
						{children}
					</Box>
				</Drawer>
			)}
		</div>
	);
};

export default SideDrawer;
